import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function AgingReportsPage() {
  const [tab, setTab] = useState("debtors");
  return (
    <div data-testid="aging-reports-page">
      <h1 className="font-heading font-bold text-2xl text-[#1F2937] mb-1">Aging Reports</h1>
      <p className="text-sm text-gray-500 mb-6">30/60/90 day analysis for debtors and creditors</p>
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button data-testid="tab-debtors" onClick={() => setTab("debtors")} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "debtors" ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>Debtors Aging</button>
        <button data-testid="tab-creditors" onClick={() => setTab("creditors")} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === "creditors" ? "border-[#E63946] text-[#E63946]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>Creditors Aging</button>
      </div>
      {tab === "debtors" ? <DebtorsAging /> : <CreditorsAging />}
    </div>
  );
}

function DebtorsAging() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get("/reports/debtors-aging").then(r => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">As at {data.report_date} &middot; {data.rows.length} customer{data.rows.length !== 1 ? "s" : ""} with outstanding balances</p>
        <Button size="sm" variant="outline" onClick={load} className="rounded-sm h-8 text-xs"><BarChart3 className="w-3 h-3 mr-1" />Refresh</Button>
      </div>
      <div className="grid grid-cols-5 gap-3 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">Current</p><p className="text-lg font-bold font-mono text-[#1F2937]">{fmt(data.totals.current)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">30 Days</p><p className="text-lg font-bold font-mono text-amber-600">{fmt(data.totals.days_30)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">60 Days</p><p className="text-lg font-bold font-mono text-orange-600">{fmt(data.totals.days_60)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">90+ Days</p><p className="text-lg font-bold font-mono text-red-600">{fmt(data.totals.days_90)}</p>{data.totals.days_90 > 0 && <AlertTriangle className="w-3.5 h-3.5 text-red-400 mx-auto mt-0.5" />}</CardContent></Card>
        <Card className="border rounded-sm border-[#E63946]/30"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">Total</p><p className="text-lg font-bold font-mono text-[#E63946]">{fmt(data.totals.total)}</p></CardContent></Card>
      </div>
      {data.rows.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No outstanding debtors</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="debtors-aging-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Customer</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Current</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">30 Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">60 Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">90+ Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
            </tr></thead>
            <tbody>{data.rows.map(r => (
              <tr key={r.customer_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/customers/${r.customer_id}`)}>
                <td className="p-3 font-medium text-[#1F2937]">{r.customer_name}</td>
                <td className="p-3 text-right font-mono">{r.current > 0 ? fmt(r.current) : "—"}</td>
                <td className="p-3 text-right font-mono text-amber-600">{r.days_30 > 0 ? fmt(r.days_30) : "—"}</td>
                <td className="p-3 text-right font-mono text-orange-600">{r.days_60 > 0 ? fmt(r.days_60) : "—"}</td>
                <td className="p-3 text-right font-mono text-red-600 font-medium">{r.days_90 > 0 ? fmt(r.days_90) : "—"}</td>
                <td className="p-3 text-right font-mono font-bold">{fmt(r.total)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr className="border-t-2 font-bold bg-gray-50/50">
              <td className="p-3">TOTAL</td>
              <td className="p-3 text-right font-mono">{fmt(data.totals.current)}</td>
              <td className="p-3 text-right font-mono text-amber-600">{fmt(data.totals.days_30)}</td>
              <td className="p-3 text-right font-mono text-orange-600">{fmt(data.totals.days_60)}</td>
              <td className="p-3 text-right font-mono text-red-600">{fmt(data.totals.days_90)}</td>
              <td className="p-3 text-right font-mono text-[#E63946]">{fmt(data.totals.total)}</td>
            </tr></tfoot>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreditorsAging() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { setLoading(true); api.get("/reports/creditors-aging").then(r => setData(r.data)).catch(() => toast.error("Failed")).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="animate-pulse h-40 bg-gray-100 rounded-sm" />;
  if (!data) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">As at {data.report_date} &middot; {data.rows.length} supplier{data.rows.length !== 1 ? "s" : ""} with outstanding balances</p>
        <Button size="sm" variant="outline" onClick={load} className="rounded-sm h-8 text-xs"><BarChart3 className="w-3 h-3 mr-1" />Refresh</Button>
      </div>
      <div className="grid grid-cols-5 gap-3 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">Current</p><p className="text-lg font-bold font-mono text-[#1F2937]">{fmt(data.totals.current)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">30 Days</p><p className="text-lg font-bold font-mono text-amber-600">{fmt(data.totals.days_30)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">60 Days</p><p className="text-lg font-bold font-mono text-orange-600">{fmt(data.totals.days_60)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">90+ Days</p><p className="text-lg font-bold font-mono text-red-600">{fmt(data.totals.days_90)}</p></CardContent></Card>
        <Card className="border rounded-sm border-[#E63946]/30"><CardContent className="p-3 text-center"><p className="text-[10px] text-gray-500 uppercase font-semibold">Total Owed</p><p className="text-lg font-bold font-mono text-[#E63946]">{fmt(data.totals.total)}</p></CardContent></Card>
      </div>
      {data.rows.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No outstanding creditors</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="creditors-aging-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Supplier</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Current</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">30 Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">60 Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">90+ Days</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
            </tr></thead>
            <tbody>{data.rows.map(r => (
              <tr key={r.supplier_id} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/suppliers/${r.supplier_id}`)}>
                <td className="p-3 font-medium text-[#1F2937]">{r.supplier_name}</td>
                <td className="p-3 text-right font-mono">{r.current > 0 ? fmt(r.current) : "—"}</td>
                <td className="p-3 text-right font-mono text-amber-600">{r.days_30 > 0 ? fmt(r.days_30) : "—"}</td>
                <td className="p-3 text-right font-mono text-orange-600">{r.days_60 > 0 ? fmt(r.days_60) : "—"}</td>
                <td className="p-3 text-right font-mono text-red-600 font-medium">{r.days_90 > 0 ? fmt(r.days_90) : "—"}</td>
                <td className="p-3 text-right font-mono font-bold">{fmt(r.total)}</td>
              </tr>
            ))}</tbody>
            <tfoot><tr className="border-t-2 font-bold bg-gray-50/50">
              <td className="p-3">TOTAL</td>
              <td className="p-3 text-right font-mono">{fmt(data.totals.current)}</td>
              <td className="p-3 text-right font-mono text-amber-600">{fmt(data.totals.days_30)}</td>
              <td className="p-3 text-right font-mono text-orange-600">{fmt(data.totals.days_60)}</td>
              <td className="p-3 text-right font-mono text-red-600">{fmt(data.totals.days_90)}</td>
              <td className="p-3 text-right font-mono text-[#E63946]">{fmt(data.totals.total)}</td>
            </tr></tfoot>
          </table>
        </div></Card>
      )}
    </div>
  );
}
